/**
 * How an interview link's interview ended (AIC-252; tracker-api sessions/outcome.ts), as one line of text:
 * "Partial: 12 of 20 answered, call dropped Sep 28, 2026, 2:32 PM", with a resume hint when the vendor can come back on
 * the same link. Used by Assignment detail; Today can show the same line from its rows' `interview_outcome`.
 */
import { formatDate } from "./common.tsx";

/** How each end reason reads after "answered, " (tracker-api sessions/outcome.ts END_REASON_LABELS). */
export const END_REASON_LABELS: Readonly<Record<string, string>> = {
  dropped: "call dropped", vendor_ended: "vendor hung up", silence_cap: "silence limit reached", call_cap: "call time limit reached",
  escalate_to_analyst: "escalated to an analyst", vendor_request: "vendor asked to stop", technical_problem: "technical problem",
  completed_partial: "call ended with questions open", closing_complete: "call closed with questions open", goodbye_loop: "call ended early",
  repeat_loop: "call ended early", busy: "another call was already open", practice_ended: "practice interview was reset", completed: "call ended", other: "call ended",
  form_left: "form left unfinished", review_left: "review not confirmed", no_end_report: "call ended (no end report)",
  superseded: "replaced by a newer link", expired: "link expired", submitted: "submitted",
};

export function endReasonLabel(r: string | null | undefined): string {
  return r ? END_REASON_LABELS[r] ?? r.replace(/_/g, " ") : "ended";
}

export interface OutcomeLike {
  outcome: string;
  end_reason?: string | null;
  answered_count?: number | null;
  answer_total?: number | null;
  ended_at?: string | null;
  resumable?: boolean;
}

const counted = (o: OutcomeLike) => (o.answered_count !== null && o.answered_count !== undefined && o.answer_total ? `${o.answered_count} of ${o.answer_total} answered` : null);

export const UNKNOWN_OUTCOME_LINE = "Started, outcome not recorded";
/** Added only for a link that has actually closed (AIC-269): an open link with no outcome has not closed at all. */
const UNKNOWN_CLOSED_NOTE = ": this link closed before interview outcomes were tracked";
const CLOSED_LINK = ["expired", "superseded"];

/**
 * The outcome line, or null for a link with nothing to say (not started). `linkStatus` is the link's (display)
 * status, when the caller has it: it decides whether an unknown outcome says the link closed.
 */
export function outcomeLine(o: OutcomeLike | null | undefined, linkStatus?: string | null): string | null {
  if (!o) return null;
  switch (o.outcome) {
    case "in_progress": return "Live now: the interview is in progress";
    case "review": return "Call finished: waiting for the vendor to confirm the review";
    case "completed": return counted(o) ? `Completed: ${counted(o)}` : "Completed";
    case "partial":
    case "abandoned": {
      const head = o.outcome === "partial" ? `Partial: ${counted(o) ?? "some answers recorded"}` : "Abandoned: no answers recorded";
      return `${head}, ${endReasonLabel(o.end_reason)} ${formatDate(o.ended_at)}`;
    }
    // AIC-269: started with no outcome recorded; "closed before ..." only when the link is expired or superseded.
    case "unknown": return linkStatus && CLOSED_LINK.includes(linkStatus) ? UNKNOWN_OUTCOME_LINE + UNKNOWN_CLOSED_NOTE : UNKNOWN_OUTCOME_LINE;
    default: return null;
  }
}

/** The hint under a partial or abandoned link the vendor can still use. */
export function resumeHint(o: OutcomeLike | null | undefined): string | null {
  if (!o?.resumable) return null;
  return "The vendor can resume on the same link: their answers so far are kept. No new link is needed.";
}
